/**
 * Formatter (v0.2) — brace-depth re-indenter + §4.11 reference canonicalization.
 *
 * Splits the document into lines, computes depth from running brace
 * count (handling tokens inside strings/comments by skipping them via
 * the lexer's token stream), and emits each non-empty line at
 * `depth * indentSize` spaces. Trailing whitespace is stripped.
 *
 * Inline structure (multiple statements on one line, brace placement
 * within strings) is preserved — only the leading whitespace of each
 * line changes — with ONE deliberate exception: interface references are
 * canonicalized to the §4.11 short form (surface segments elided when the
 * leaf is unique; see format-elision.ts). That pass is file-local and runs
 * only on cleanly-parsing sources.
 */

import type { Range, TextEdit } from "vscode-languageserver";
import type { Token } from "@archlang/engine";
import type { FileState } from "../workspace/workspace.js";
import { canonicalizeElisions } from "./format-elision.js";

export type FormatterOptions = {
  readonly indentSize?: number;
  readonly tabSize?: number;
  readonly insertSpaces?: boolean;
};

export function formatDocument(
  file: FileState,
  _options: FormatterOptions = {}
): TextEdit[] {
  const indentSize = _options.indentSize ?? 4;
  const indentChar = _options.insertSpaces === false ? "\t" : " ";
  const indentUnit = _options.insertSpaces === false ? "\t" : indentChar.repeat(indentSize);

  const text = file.text;
  const lineStarts = computeLineStarts(text);
  const depthByLine = computeDepthPerLine(file.tokens, lineStarts);
  // Lines that lie *inside* a multi-line string literal must be left
  // verbatim — they're content, not code. The lexer's
  // `normalizeMultilineString` strips a uniform leading-whitespace
  // prefix at parse time, so any indentation we add here would either
  // be silently stripped (no-op) or, worse, would flatten author-chosen
  // visual structure inside the string. Mark every interior line of
  // every multi-line STRING token here.
  const insideMultilineString = computeMultilineStringMask(file.tokens, lineStarts.length);

  const formatted: string[] = [];
  for (let i = 0; i < lineStarts.length; i++) {
    const start = lineStarts[i]!;
    const end = i + 1 < lineStarts.length ? lineStarts[i + 1]! - 1 : text.length;
    const raw = text.slice(start, end);

    if (insideMultilineString[i]) {
      // Preserve the exact original line — every byte of it. Code
      // outside the string still re-indents above and below.
      formatted.push(raw);
      continue;
    }

    const stripped = raw.replace(/^[ \t]+/, "").replace(/[ \t]+$/, "");
    if (stripped === "") {
      formatted.push("");
      continue;
    }
    const depth = Math.max(0, depthByLine[i] ?? 0);
    formatted.push(indentUnit.repeat(depth) + stripped);
  }

  // `lineStarts` already accounts for the final newline (the trailing
  // empty line in `formatted` corresponds to it), so `join("\n")` alone
  // reproduces the original line count. Don't double-append "\n".
  //
  // §4.11 canonicalization runs on the RE-INDENTED text (a fresh parse inside,
  // so spans are exact); it is a no-op for files with parse errors.
  const newText = canonicalizeElisions(formatted.join("\n"));
  if (newText === text) return [];

  const lastLine = lineStarts.length - 1;
  const lastLineStart = lineStarts[lastLine]!;
  const lastLineLength = text.length - lastLineStart;
  const range: Range = {
    start: { line: 0, character: 0 },
    end: { line: lastLine, character: lastLineLength },
  };
  return [{ range, newText }];
}

function computeLineStarts(text: string): number[] {
  const starts = [0];
  for (let i = 0; i < text.length; i++) {
    if (text[i] === "\n") starts.push(i + 1);
  }
  return starts;
}

/**
 * For each line index, the brace-depth at the START of that line. We
 * walk the token stream so braces inside strings/comments are
 * automatically excluded.
 */
function computeDepthPerLine(
  tokens: readonly Token[],
  lineStarts: readonly number[]
): number[] {
  const depthAtLine = new Array<number>(lineStarts.length).fill(0);
  let depth = 0;
  let lineIdx = 0;
  for (const tok of tokens) {
    const tokLine = tok.span.start.line - 1;
    while (lineIdx < tokLine) {
      lineIdx++;
      depthAtLine[lineIdx] = depth;
    }
    if (tok.type === "LBRACE") {
      depth++;
    } else if (tok.type === "RBRACE") {
      // For lines that BEGIN with `}`, we want indent at depth-1.
      const lineStart = lineStarts[tokLine] ?? 0;
      const isFirstNonWhitespace = isFirstOnLine(tok, lineStart);
      if (isFirstNonWhitespace) {
        depthAtLine[tokLine] = Math.max(0, depth - 1);
      }
      depth = Math.max(0, depth - 1);
    }
  }
  // Fill remaining lines with the trailing depth.
  for (let i = lineIdx + 1; i < depthAtLine.length; i++) {
    depthAtLine[i] = depth;
  }
  return depthAtLine;
}

/**
 * For each line, mark whether it lies strictly inside a multi-line
 * string literal — i.e. the line is between a STRING token's start
 * line and end line, exclusive of the start line itself (the start
 * line still has surrounding context that should be re-indented).
 *
 * The end line *is* included when the closing quote isn't on the
 * line's last column, because the line is still "string content"
 * even if the quote terminates partway through. Conservative: we'd
 * rather leave a sometimes-formattable line alone than mangle a
 * deliberately-laid-out template.
 */
function computeMultilineStringMask(
  tokens: readonly Token[],
  lineCount: number
): boolean[] {
  const mask = new Array<boolean>(lineCount).fill(false);
  for (const tok of tokens) {
    if (tok.type !== "STRING") continue;
    const startLine = tok.span.start.line - 1;
    const endLine = tok.span.end.line - 1;
    if (endLine <= startLine) continue;
    // Mark every interior line. The start line keeps its outer-code
    // indentation (anything before the opening quote is real code).
    for (let i = startLine + 1; i <= endLine; i++) {
      if (i >= 0 && i < lineCount) mask[i] = true;
    }
  }
  return mask;
}

function isFirstOnLine(tok: Token, lineStart: number): boolean {
  // The token is the first non-trivia thing on its line if all leading
  // trivia for the token starts at or after `lineStart` and only
  // contains whitespace (no tokens before).
  for (const t of tok.leadingTrivia) {
    if (t.kind !== "whitespace") {
      // Comment on same line would still allow `}` to be first
      // structural; treat as not-first to be safe.
      if (t.span.start.line === tok.span.start.line) return false;
    }
  }
  // Span between lineStart and tok.span.start is whitespace (we can't
  // verify directly here without text; trust the lexer's trivia).
  return tok.span.start.offset >= lineStart;
}
